import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Template } from "./Template";
import { useLoggedInUser } from "../user/LoggedInUserProvider";
import { LazyUserListPage } from "@/user/super/lazySuperPages";
import { Fragment, ReactElement, ReactNode, Suspense } from "react";
import { LazyFirmSettingsPage } from "@/firm/LazyFirmSettingsPage";
import {
    LazyAssessmentPage,
    LazyAssessmentListPage
} from "@/assessment/lazyAssessmentComponents";
import { LazyNoFirmPage, LazyUserProfilePage } from "@/user/lazyUserPages";
import { NotFound } from "./NotFound";
import { LazyFirmListPage, LazyFirmPage } from "@/firm/super/lazySuperPages";
import {
    LazyAssessmentTypeListPage,
    LazyAssessmentTypePage,
    LazyAssessmentTypeVersionPage
} from "@/assessmentType/super/lazySuperPages";
import {
    LazyEvaluationBackgroundRefetch,
    LazyEvaluationResultsPage
} from "@/evaluation/lazyComponents";
import { LazyReviewPage } from "@/review/lazyComponents";
import { LazyResultsPage } from "@/results/lazyComponents";
import { LazyQuestionPage } from "@/question/lazyComponents";
import { useFirmId } from "@/firm/FirmProvider";
import { UserThemeSetter } from "@/user/UserThemeSetter";
import { PreloadAssessments } from "@/assessment/PreloadAssessments";
import { LazyPrepopulationBackgroundRefetch } from "@/prepopulation/lazyComponents";
import { LazyAggregationBackgroundRefetch } from "@/aggregation/lazyComponents";
import { useAuth } from "react-oidc-context";
import { AutoLogout } from "@/user/AutoLogout";

interface IRoute {
    path: string;
    element: ReactElement;
    allowAnyone?: boolean;
}

export const assessmentPagePath = "/assessments/:assessmentId";
export const questionPagePath =
    "/assessments/:assessmentId/teams/:teamId/questions/:questionKey";
export const reviewPagePath = "/assessments/:assessmentId/teams/:teamId/review";
export const resultsPagePath =
    "/assessments/:assessmentId/teams/:teamId/results";

export const routes: IRoute[] = [
    {
        path: "/profile",
        element: <LazyUserProfilePage />,
        allowAnyone: true
    },
    {
        path: "/firm/settings",
        element: <LazyFirmSettingsPage />,
        allowAnyone: true
    },
    {
        path: "/",
        element: <LazyAssessmentListPage />,
        allowAnyone: true
    },
    {
        path: assessmentPagePath,
        element: <LazyAssessmentPage />,
        allowAnyone: true
    },
    {
        path: questionPagePath,
        element: <LazyQuestionPage />,
        allowAnyone: true
    },
    {
        path: resultsPagePath,
        element: <LazyResultsPage />,
        allowAnyone: true
    },
    {
        path: "/assessments/:assessmentId/evaluations/:requestId/results",
        element: <LazyEvaluationResultsPage />
    },
    {
        path: reviewPagePath,
        element: <LazyReviewPage />
    },
    {
        path: "/super/users",
        element: <LazyUserListPage />
    },
    {
        path: "/super/firms",
        element: <LazyFirmListPage />
    },
    {
        path: "/super/firms/:firmId",
        element: <LazyFirmPage />
    },
    {
        path: "/super/assessment-types",
        element: <LazyAssessmentTypeListPage />
    },
    {
        path: "/super/assessment-types/:assessmentTypeId",
        element: <LazyAssessmentTypePage />
    },
    {
        path: "/super/assessment-types/:assessmentTypeId/versions/:versionId",
        element: <LazyAssessmentTypeVersionPage />
    }
];

export const Router = () => {
    return (
        <Routes>
            <Route path="/logout" element={<AutoLogout />} />
            <Route element={<ProtectedTemplate />}>
                {routes.map(({ path, element, allowAnyone }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <UniqueKeyForParamsRoute>
                                {allowAnyone ? (
                                    element
                                ) : (
                                    <ProtectedSuperRoute>
                                        {element}
                                    </ProtectedSuperRoute>
                                )}
                            </UniqueKeyForParamsRoute>
                        }
                    />
                ))}
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
};

const ProtectedTemplate = () => {
    const { isAuthenticated } = useAuth();
    const { hasFirmId } = useFirmId();

    if (!isAuthenticated) {
        // shouldn't have got this far, but just in case
        return null;
    }

    if (!hasFirmId) {
        return <LazyNoFirmPage />;
    }

    return (
        <>
            <Template />
            <BackgroundDataHandling />
        </>
    );
};

const BackgroundDataHandling = () => {
    return (
        <>
            {/* UserThemeSetter not inside Suspense so that top level loading boundary still shows before a possible theme switch */}
            <UserThemeSetter />
            {/* Background refetches / preloading */}
            <Suspense>
                <PreloadAssessments />
                <LazyPrepopulationBackgroundRefetch />
                <LazyEvaluationBackgroundRefetch />
                <LazyAggregationBackgroundRefetch />
            </Suspense>
        </>
    );
};

const ProtectedSuperRoute = ({ children }: { children: ReactNode }) => {
    const { isSuper } = useLoggedInUser();
    return isSuper ? children : <Navigate to="/" replace />;
};

// ensure that the route is re-rendered when the params change
const UniqueKeyForParamsRoute = ({ children }: { children: ReactNode }) => {
    const params = useParams();
    const key = JSON.stringify(params);
    return <Fragment key={key}>{children}</Fragment>;
};
